import * as moment from 'moment-timezone';
import 'moment/locale/nl';
import { validate } from 'validate.js';
import Quote from './quote';
import Booking from './booking';
import Utils from './utils';

const { __, _n, sprintf } = wp.i18n;

/**
 * @todo Create a date object for departure, arrival
 */
class QuickParking {
  constructor() {
    /**
     * @property Departure
     */
    this.departure = null;

    /**
     * @property Arrival
     */
    this.arrival = null;

    /**
     * @property Options
     * Object passed by PHP
     */
    this.options = options;

    /**
     * @property Decimal Formatter
     */
    this.decimalFormatter = new Intl.NumberFormat('nl-NL', {
      style: 'decimal',
      minimumFractionDigits: 2,
    });

    /**
     * @property Form constraints
     */
    this.formConstraints = {
      'qp-first-name': {
        presence: true,
      },
      'qp-last-name': {
        presence: true,
      },
      'qp-email': {
        presence: true,
      },
      'qp-phone': {
        presence: true,
      },
      'qp-passengers': {
        presence: true,
      },
      'qp-agree-tc': {
        presence: true,
        inclusion: {
          within: [true],
          message: __('De algemene voorwaarden moeten geaccepteerd worden', 'quickparking'),
        },
      },
    };

    // Selectors
    this.selectors = [];
    this.selectors.wrapper = $('.qp-wrap');
    this.selectors.dateForm = $('.qp-date-range');
    this.selectors.extendedForm = $('.qp-module');
    this.selectors.quote = $('.qp-quote', this.selectors.extendedForm);
    this.selectors.priceField = $('.qp-total', this.selectors.quote);
    this.selectors.subTotalField = $('.qp-subtotal', this.selectors.quote);
    this.selectors.discountField = $('.qp-discount', this.selectors.quote);
    this.selectors.daysField = $('.qp-days', this.selectors.quote);

    this.selectors.couponField = $('.qp-coupon input', this.selectors.quote);
    this.selectors.couponInputWrap = $('.qp-coupon-input-wrap', this.selectors.quote);
    this.selectors.couponToggle = $('.qp-coupon-toggle', this.selectors.quote);
    this.selectors.couponActivation = $('.qp-apply-coupon', this.selectors.quote);

    this.selectors.error = $('.qp-error');
  }

  init() {
    moment.locale('nl');

    /**
     * Creates a new quote based on date
     */
    this.selectors.dateForm.on('submit', (e) => {
      e.preventDefault();

      if (this.handleDateInput()) {
        this.createNewQuote(this.departure, this.arrival);
      }
    });

    /**
     * Reset the submit button in the date form when values change
     */
    $('input', this.selectors.dateForm).on('change', () => this.resetDateRangeButton());
    
    /**
     * Applies the coupon code and requests a new quote
     * 
     * Handles the date input again so users can't
     * change their input in between.
     */
    this.selectors.couponActivation.on('click', () => {
      this.handleDateInput();
      this.applyCoupon();
    });

    /**
     * Toggles the coupon input
     */
    this.selectors.couponToggle.on('click', () => {
      this.selectors.couponInputWrap.slideToggle(250);
    });

    /** 
     * Checks the user submitted info
     * and requests a Quick Parking Booking
     */
    this.selectors.extendedForm.on('submit', (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      const errors = validate(form, this.formConstraints);

      if (errors) {
        this.showError(__('Het formulier is niet verstuurd, wil je onderstaande velden ook invullen?', 'quickparking'));

        // Mark fields
        Object.keys(errors).forEach((error) => {
          $(`input[name=${error}], select[name=${error}], checkbox[name=${error}]`)
            .parents('.form-group')
            .addClass('qp-form-error');
        });
      } else {
        this.createNewBooking();
      }
    });

    // After handlers have been set we can
    // safely submit forms with Javascript
    this.checkInitialValues();
  }

  updateQuoteDisplay(data) {
    if (data.products[0] === undefined) {
      this.showError(__('Er kon geen prijs opgehaald worden voor de gekozen data. Probeer het nog een keer.', 'quickparking'));
    } else if (data.products[0] && data.products[0].isSoldOut) {
      this.showError(__('Er zijn helaas geen parkeerplaatsen meer beschikbaar voor de opgegeven periode.', 'quickparking'));
      this.selectors.extendedForm.hide();
    } else {
      this.showQuote();
      this.updateQuoteDays();
      this.updateQuotePrices(data.products[0].pricing);
      this.updateDateRangeButton(data.products[0].pricing);
      this.checkCoupon(data.products[0].pricing);
    }
  }

  updateQuoteDays() {
    const { departure, arrival } = this;

    /* eslint-disable prefer-arrow-callback */
    this.selectors.daysField.html(function setContent() {
      let duration = arrival.diff(departure.clone().startOf('day'), 'days');

      if (arrival.hour() >= 3) {
        duration += 1;
      }

      /* eslint-disable max-len */
      const durationString = sprintf(_n('%s dag voordelig parkeren', '%s dagen voordelig parkeren', duration, 'quickparking'), duration);
      /* eslint-enable max-len */

      return `
        <strong>${durationString}</strong><br>
        ${departure.format('D MMMM').toLowerCase()} - ${arrival.format('D MMMM').toLowerCase()}
      `;
    });
    /* eslint-enable prefer-arrow-callback */
  }

  updateQuotePrices(pricing) {
    const prices = [
      {
        value: pricing.regularPrice,
        field: this.selectors.subTotalField,
        dependencies: [
          pricing.couponDiscount.amount,
        ],
      },
      {
        value: pricing.couponDiscount.amount,
        field: this.selectors.discountField,
      },
      {
        value: pricing.totalPrice,
        field: this.selectors.priceField,
      },
    ];

    prices.forEach((price) => {
      if (price.value !== 0) {
        price.field.text(this.decimalFormatter.format(price.value));

        // If all price dependencies are false, don't show the field
        // relying on them
        if (price.dependencies && !price.dependencies.some(element => element > 0)) {
          price.field.parent().hide();
        } else {
          price.field.parent().show(0);
        }
      } else {
        price.field.parent().hide();
      }
    });
  }

  applyCoupon() {
    this.couponCode = this.selectors.couponField.val();
    this.createNewQuote(this.departure, this.arrival, this.couponCode);
  }

  createNewQuote(departure, arrival, coupon = null) {
    /* eslint-disable max-len */
    const xhr = new Quote(Utils.formatTimeForApi(departure), Utils.formatTimeForApi(arrival), coupon);
    /* eslint-enable max-len */
    const loadingApplicable = [
      this.selectors.wrapper,
    ];

    this.resetErrors();
    this.constructor.setLoading(...loadingApplicable);

    xhr.then((data) => {
      this.quote = data;
      this.updateQuoteDisplay(data);
    }, (error) => {
      /* eslint-disable no-console */
      console.error(error);
      /* eslint-enable no-console */
    }).always(() => {
      this.constructor.removeLoading(...loadingApplicable);
    });
  }

  createNewBooking() {
    const inputs = Utils.getFormData(this.selectors.extendedForm);
    const loadingApplicable = [
      this.selectors.wrapper,
    ];

    if (!Object.entries(inputs).length || !this.quote) {
      return;
    }

    this.constructor.setLoading(...loadingApplicable);

    const bookingData = {
      customer: {
        firstName: inputs['qp-first-name'],
        lastName: inputs['qp-last-name'],
        email: inputs['qp-email'],
        mobileNumber: inputs['qp-phone'],
        numberOfPersons: inputs['qp-passengers'],
      },
      enterDateTime: Utils.formatTimeForApi(this.departure),
      exitDateTime: Utils.formatTimeForApi(this.arrival),
      labelAbbreviation: this.options.labelAbbreviation || 'QPA',
      locationAbbreviation: this.options.locationAbbreviation || 'AMS',
      paymentLocation: 'onLineLater',
      productAbbreviation: this.quote.products[0].abbreviation,
    };

    const xhr = new Booking(bookingData);
    
    xhr.then((booking) => {
      if (booking.isValid) {
        const priceExcludingVat = booking.pricing.totalPrice - booking.pricing.vatAmount;

        // Everything is in order, redirect the user
        window.location.href = `${this.options.thankYouPage}/?qpd=${moment(booking.enterDateTime).format('D MMMM')}&qpdm=${moment(booking.enterDateTime).format('M')}&qpp=${booking.pricing.totalPrice.toString()}&qppev=${priceExcludingVat.toString()}&qpb=${booking.code}`;
      } else if (booking.errors) {
        Object.keys(booking.errors).forEach((key) => {
          this.showMappedError(key);
        });
      } else {
        // invalid booking without errors is probably a bookgin the has to be prepaid
        this.showError(__('U ontvangt van ons een e-mail met daarin instructies om de boeking te voltooien.', 'quickparking'));
        this.constructor.removeLoading(...loadingApplicable);
      }

      this.booking = booking;
    }, (error) => {
      /* eslint-disable no-console */
      console.error(error);
      /* eslint-enable no-console */
      this.constructor.removeLoading(...loadingApplicable);
    });
  }

  handleDateInput() {
    const inputs = Utils.getFormData(this.selectors.dateForm);

    this.setDeparture(inputs['qp-departure-date'], inputs['qp-departure-time']);
    this.setArrival(inputs['qp-arrival-date'], inputs['qp-arrival-time']);

    // Returns true if every input has a value
    return Object.entries(inputs).every(value => value[1].length);
  }

  setDeparture(date, time) {
    this.departure = Utils.convertDateTime(date, time);
  }

  setArrival(date, time) {
    this.arrival = Utils.convertDateTime(date, time);
  }

  /**
   * Checks to see if the date range form has values
   * on init
   */
  checkInitialValues() {
    if (this.handleDateInput()) {
      this.selectors.dateForm.submit();
    }
  }

  static setLoading(...selectors) {
    selectors.forEach((element) => {
      element.addClass('qp-loading-wrap');
      element.append('<div class="qp-loading"><div class="qp-loader"><div class="bounce1"></div><div class="bounce2"></div><div class="bounce3"></div></div></div>');
    });
  }

  static removeLoading(...selectors) {
    selectors.forEach((element) => {
      element.removeClass('qp-loading-wrap');
      $('.qp-loading', element).remove();
    });
  }

  showMappedError(errorCode) {
    const errorMap = {
      'customer.email': __('E-mailadres is onjuist', 'quickparking'),
      'customer.numberOfPersons': __('Aantal passagiers is onjuist', 'quickparking'),
    };

    this.showError(errorMap[errorCode] || __('Er is iets fout gegaan', 'quickparking'));
  }

  showError(error) {
    if (error.length) {
      const previousText = this.selectors.error.html();

      this.selectors.error
        .html(`
          ${previousText}
          ${error}<br>
        `)
        .show();
    }
  }
  
  resetErrors() {
    this.selectors.error
      .text('')
      .hide();
  }

  showQuote() {
    this.selectors.quote.show();
    this.selectors.extendedForm.show();
  }

  updateDateRangeButton(pricing) {
    const button = $('button', this.selectors.dateForm);
    const price = this.decimalFormatter.format(pricing.totalPrice);

    button
      .text(`${__('Mijn prijs', 'quickparking')}: € ${price}`)
      .addClass('btn-info')
      .attr('type', 'button');
  }

  resetDateRangeButton() {
    const button = $('button', this.selectors.dateForm);

    button
      .text(__('Bereken mijn prijs', 'quickparking'))
      .removeClass('btn-info')
      .attr('type', 'submit');
  }

  checkCoupon(pricing) {
    this.selectors.couponInputWrap.removeClass('qp-form-error');

    if (pricing.couponDiscount && pricing.couponDiscount.couponCode) {
      if (!pricing.couponDiscount.isValid) {
        this.selectors.couponInputWrap.addClass('qp-form-error');
      }
    }
  }
}

$(document).ready(() => new QuickParking().init());
